import React from 'react';
import { arrayOf, bool, func, node, number, object, shape, string } from 'prop-types';
import classNames from 'classnames';
import { LinkedLogo, NamedLink } from '../../../../components';
import logo from '../../../../assets/logo_footer.png';
import logoNew from '../../../../assets/NewLogos/Layer_1.svg';

import Field from '../../Field';
import BlockBuilder from '../../BlockBuilder';

import SectionContainer from '../SectionContainer';
import css from './SectionFooter.module.css';
import { useSelector } from 'react-redux';

// The number of columns (numberOfColumns) affects styling

const GRID_CONFIG = [
  { contentCss: css.contentCol1, gridCss: css.gridCol1 },
  { contentCss: css.contentCol2, gridCss: css.gridCol2 },
  { contentCss: css.contentCol3, gridCss: css.gridCol3 },
  { contentCss: css.contentCol4, gridCss: css.gridCol4 },
];
const MAX_MOBILE_SCREEN_WIDTH = 1024;

const getIndex = numberOfColumns => numberOfColumns - 1;

const getContentCss = numberOfColumns => {
  const contentConfig = GRID_CONFIG[getIndex(numberOfColumns)];
  return contentConfig ? contentConfig.contentCss : GRID_CONFIG[0].contentCss;
};

const getGridCss = numberOfColumns => {
  const contentConfig = GRID_CONFIG[getIndex(numberOfColumns)];
  return contentConfig ? contentConfig.gridCss : GRID_CONFIG[0].gridCss;
};

// Section component that's able to show blocks in multiple different columns (defined by "numberOfColumns" prop)
const SectionFooter = props => {
  const {
    sectionId,
    className,
    rootClassName,
    numberOfColumns,
    socialMediaLinks,
    slogan,
    appearance,
    copyright,
    blocks,
    options,
    isFooter,
    linkLogoToExternalSite,
  } = props;
  const state = useSelector(state => state);
  const { currentUser } = state.user;
  // console.log('currentUser', currentUser);
  const content = null;
  currentUser
    ? '- [Sell Now](/l/new)\n- [Shop Now](/s)'
    : '- [Sell Now](/l/new)\n- [Shop Now](/s)\n- [Login](/login)';
  const blockshardCode = [
    {
      blockType: 'footerBlock',
      blockId: 'general',
      text: {
        content: content,
        fieldType: 'markdown',
      },
    },
  ];
  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };
  const linksWithBlockId = socialMediaLinks?.map(sml => {
    return {
      ...sml,
      blockId: sml.link.platform,
    };
  });

  const showSocialMediaLinks = socialMediaLinks?.length > 0;
  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout = hasMatchMedia
    ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
    : true;
  const logoLayout = isMobileLayout ? 'mobile' : 'desktop';

  // use block builder instead of mapping blocks manually

  const modifiedBlocks = blocks?.map(block => {
    if (block && block.text && typeof block.text.content === 'string') {
      return {
        ...block,
        text: {
          ...block.text,
          content: currentUser 
            ? block.text.content.replace(/\[Login\]\(\/login\)/g, '[Logout](/logout)') 
            : block.text.content.replace(/\[Logout\]\(\/logout\)/g, '[Login](/login)')
        }
      };
    }
    return block;
  });

  return (
    <SectionContainer
      as="footer"
      id={sectionId}
      className={className || css.root}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      <div className={css.footer}>
        <div className={css.logoContainer}>
          <NamedLink name="LandingPage" className={css.logoLink}>
            <img className={css.logoImage} src={logoNew} alt="logo" />
          </NamedLink>
          <div className={css.linksFooter}>
            <BlockBuilder
              blocks={modifiedBlocks}
              sectionId={sectionId}
              options={options}
              className={css.footerH3}
            />
          </div>
          <div className={css.sloganMobile}>
            <Field data={slogan} sectionId={sectionId} className={css.slogan} />
          </div>
          <div className={css.detailsInfo}>
            <div className={css.sloganDesktop}>
              
              <Field data={slogan} sectionId={sectionId} className={css.slogan} />
            </div>
            {showSocialMediaLinks ? (
              <div className={css.icons}>
                <BlockBuilder blocks={linksWithBlockId} sectionId={sectionId} options={options} />
              </div>
            ) : null}
            {/* <Field data={copyright} className={css.copyright} /> */}
          </div>
        </div>
        <div className={css.textContainer}>
          <span>WHY JOIN THE LIKE NEW ONLY COMMUNITY?</span>
          <p>
            Sellers get to enjoy that zen feeling when our closets are clutter-free knowing our
            preloved pieces have found a new home. Shoppers get to pick up luxurious pieces for a
            fraction of the retail cost. Plus a portion of every transaction goes to charity? Win
            Win Win!
          </p>
        </div>
        <div className={css.textContainer}>
          <span>CHARITY</span>
          <p>
            We have selected Healthiest Babies Possible as our 1st charity.
            <br />
            <a
              href="https://www.options.bc.ca/program/healthiest-babies-possible"
              className={css.charityLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              Learn more about Healthiest Babies Possible here
            </a>
          </p>
        </div>
      </div>
      {/* <BlockBuilder blocks={blockshardCode} sectionId={sectionId} options={options} /> */}
    </SectionContainer>
  );
};

const propTypeOption = shape({
  fieldComponents: shape({ component: node, pickValidProps: func }),
});

SectionFooter.defaultProps = {
  className: null,
  rootClassName: null,
  textClassName: null,
  numberOfColumns: 1,
  socialMediaLinks: [],
  slogan: null,
  copyright: null,
  appearance: null,
  blocks: [],
  options: null,
};

SectionFooter.propTypes = {
  sectionId: string.isRequired,
  className: string,
  rootClassName: string,
  numberOfColumns: number,
  socialMediaLinks: arrayOf(object),
  slogan: object,
  copyright: object,
  appearance: object,
  blocks: arrayOf(object),
  options: propTypeOption,
};

export default SectionFooter;
